import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";

interface DataStudioTableIdentity {
  id: string;
  name: string;
}

interface DataStudioFilterableTableIdentity extends DataStudioTableIdentity {
  source: {
    name: string;
  };
}

export const DATA_STUDIO_TABLE_PARAM = "table";
export const DATA_STUDIO_MODE_PARAM = "mode";
export type DataStudioViewMode = "preview" | "data-prep";
export type DataStudioTableMenu = "filter" | "sort";

/**
 * The sort orders the asset search API can honour. It accepts `sort_by=name`
 * only, so the recency and usage orders the design showed have been dropped
 * rather than faked client-side — see `DATA_STUDIO_API_ALIGNMENT_PLAN.md` §7 Q1.
 */
export type DataStudioSortOption = "alphabeticalAsc" | "alphabeticalDesc";

/** Maps the menu selection onto the API's `sort_order`. */
export const toDataStudioSortOrder = (
  sortOption: DataStudioSortOption,
): "asc" | "desc" => (sortOption === "alphabeticalDesc" ? "desc" : "asc");

export const ALL_DATA_STUDIO_SOURCES = "__all_sources__";

export const resolveSelectedDataStudioTable = <T extends DataStudioTableIdentity>(
  tables: T[],
  requestedTableId: string | null,
): T | null =>
  tables.find((table) => table.id === requestedTableId) ?? tables[0] ?? null;

export const useDataStudioTableSelection = <T extends DataStudioTableIdentity>(
  tables: T[],
) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedTable = resolveSelectedDataStudioTable(
    tables,
    searchParams.get(DATA_STUDIO_TABLE_PARAM),
  );

  const selectTable = (tableId: string) => {
    setSearchParams(
      (currentParams) => {
        const nextParams = new URLSearchParams(currentParams);
        nextParams.set(DATA_STUDIO_TABLE_PARAM, tableId);
        return nextParams;
      },
      { replace: true },
    );
  };

  return { selectedTable, selectTable };
};

export const useDataStudioViewMode = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const mode: DataStudioViewMode =
    searchParams.get(DATA_STUDIO_MODE_PARAM) === "data-prep"
      ? "data-prep"
      : "preview";

  const setMode = (nextMode: DataStudioViewMode) => {
    setSearchParams(
      (currentParams) => {
        const nextParams = new URLSearchParams(currentParams);
        nextParams.set(DATA_STUDIO_MODE_PARAM, nextMode);
        return nextParams;
      },
      { replace: true },
    );
  };

  return { mode, setMode };
};

/**
 * Narrows an already-searched page to one data source.
 *
 * Name search and ordering now happen server-side (the API searches the whole
 * tenant, not just the loaded page). Source filtering stays client-side
 * deliberately: the source menu is built from the sources present in the
 * current results, so filtering to one of them never needs a round trip.
 */
export const filterDataStudioTables = <T extends DataStudioFilterableTableIdentity>(
  tables: T[],
  sourceName = ALL_DATA_STUDIO_SOURCES,
): T[] =>
  sourceName === ALL_DATA_STUDIO_SOURCES
    ? tables
    : tables.filter((table) => table.source.name === sourceName);

export const getDataStudioSourceNames = <
  T extends DataStudioFilterableTableIdentity,
>(
  tables: T[],
): string[] => Array.from(new Set(tables.map((table) => table.source.name)));

export const useDataStudioTablesPanel = <
  T extends DataStudioFilterableTableIdentity,
>(
  tables: T[],
) => {
  const [query, setQuery] = useState("");
  const [openMenu, setOpenMenu] = useState<DataStudioTableMenu | null>(null);
  const [sourceFilter, setSourceFilter] = useState(ALL_DATA_STUDIO_SOURCES);
  const [sortOption, setSortOption] =
    useState<DataStudioSortOption>("alphabeticalAsc");
  const { selectedTable, selectTable } = useDataStudioTableSelection(tables);
  const sourceNames = useMemo(() => getDataStudioSourceNames(tables), [tables]);
  const visibleTables = filterDataStudioTables(tables, sourceFilter);

  const toggleMenu = (menu: DataStudioTableMenu) => {
    setOpenMenu((currentMenu) => (currentMenu === menu ? null : menu));
  };

  const closeMenus = () => setOpenMenu(null);

  const selectSourceFilter = (sourceName: string) => {
    setSourceFilter(sourceName);
  };

  const selectSortOption = (nextSortOption: DataStudioSortOption) => {
    setSortOption(nextSortOption);
  };

  return {
    query,
    setQuery,
    /** Fed to the search API by the caller, debounced at the query layer. */
    searchTerm: query,
    sortOrder: toDataStudioSortOrder(sortOption),
    selectedTable,
    selectedTableId: selectedTable?.id ?? null,
    selectTable,
    visibleTables,
    sourceNames,
    sourceFilter,
    sortOption,
    openMenu,
    toggleMenu,
    closeMenus,
    selectSourceFilter,
    selectSortOption,
  };
};
